import { GridSortItem, GridSortModel } from "@mui/x-data-grid-pro";
import { IDashboardSettings } from "components/preferences";
import { Sort, sortsEqual } from "models/grids";
import { SetPreferenceSignature } from "hooks/preferences";
import { useCallback, useEffect, useMemo, useState } from "react";

type SortChangeHandler = (model: GridSortModel) => void;

type UseGridSortingProperties = {
    defaultSort?: Sort[],
    settings?: IDashboardSettings | null;
    setSettings?: SetPreferenceSignature | null
};
type UseGridSortingResult = [
    Sort[],
    SortChangeHandler,
    GridSortModel
];
type UseGridSortingSignature = (props: UseGridSortingProperties) => UseGridSortingResult;

const useGridSorting: UseGridSortingSignature = (props) => {
    const { defaultSort, settings, setSettings } = props;
    const [ value, setValue ] = useState<Sort[]>(defaultSort ?? []);
    const [ sortModel, setSortModel ] = useState<GridSortModel>([]);

    const defaultSortModel = useMemo<GridSortModel | null>(() => {
        if (defaultSort) {
            let newModel: GridSortItem[] = [];

            for (let item of defaultSort) {
                newModel.push({
                   field: item.field,
                   sort: item.dir 
                });
            }

            return newModel;
        } else {
            return null;
        }
    }, [ defaultSort ]);

    useEffect(() => {
        if (settings) {
            let newModel: GridSortItem[] = [];
            const newValue = settings.sort && settings.sort.length != 0 ? settings.sort : defaultSort ?? [];
            
            setValue(newValue);
            
            for (let item of newValue) {
                newModel.push({
                    field: item.field,
                    sort: item.dir
                });
            }

            setSortModel(newModel);
        }
    }, [ settings ]);

    const handleSortChange: SortChangeHandler = useCallback<SortChangeHandler>((model) => {        
        let newSort: Sort[] = [];
        
        let persist = false;
        if (model.length === 0) {
            newSort.push(...defaultSort ?? []);
            model = defaultSortModel ?? [];
        } else {
            persist = true;
            for (let sort of model) {
                const sortColumn = sort?.field || 'id';
                const sortDirection = sort?.sort || 'asc';

                newSort.push({
                    field: sortColumn,
                    dir: sortDirection
                });
            }
        }

        if (settings && setSettings && (!settings.sort || !sortsEqual(newSort, settings.sort))) {            
            if (!sortsEqual(defaultSort ?? [], newSort)) {
                console.trace('calling setsettings in handle sortchange');
                setSettings({
                    ...settings,
                    sort: persist ? newSort : []
                });
            } else {
                setSettings({
                    ...settings,
                    sort: []
                });
            }
        }

        if (sortsEqual(value, newSort)) return;

        console.trace('calling setValue and setSortModel in handle sortchange');
        setValue(newSort);
        setSortModel(model);
    }, [ settings, defaultSort, value ]);

    return [
        value,
        handleSortChange,
        sortModel
    ];
}

export default useGridSorting;